import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Modal, Title } from 'components';
import { useBreakpoint } from 'hooks';

import { IAppointmentConfig } from '@bb/domain/dist/types';
import { kebabCase } from 'lodash';
import { zPositions } from '@/constants';
import {
  ContactStateContext,
  ContactStateContextProvider,
  ContactModalStateContextProvider,
} from './contexts';
import { ContactStateType, ContactModalStateType } from './types';
import { ContactModalMode } from './enums';
import { isClientContactType } from './utils';

import AgentContent from './AgentContent';
import ContactContent from './ContactContent';
import ConciergeContent from './ConciergeContent';
import ModalFooter from './ModalFooter';
import SavingLoader from './SavingLoader';

export type ConfigContactModalProps = {
  contact?: Partial<ContactStateType>;
  contacts?: IAppointmentConfig['contacts'];
  isProfile?: boolean;
  addBuyerContact?: boolean;
  /**
   * used with isProfile to prevent user from being selected
   */
  userId?: string;
  isSaving: boolean;
  listingId: string;
  listing: any;
  visible: boolean;
  onSave: (
    contact: ContactStateType,
    mode: ContactModalMode,
    shouldClone: boolean,
    isEditMode?: boolean,
  ) => void;
  onClose: () => void;
  // TODO: When fully migrated to React, use LayoutContext directly instead
  setChatVisibility?: React.Dispatch<React.SetStateAction<boolean>>;
  feedbackSendToSeller: boolean;
  isMustCallDisabled: boolean;
};

const ConfigContactModal: React.FC<ConfigContactModalProps> = ({
  contact,
  contacts,
  isProfile,
  addBuyerContact = false,
  isSaving,
  listingId,
  userId,
  listing,
  visible,
  onSave,
  onClose,
  setChatVisibility,
  feedbackSendToSeller,
  isMustCallDisabled,
}) => {
  const [contactState, setContactState] = useState<ContactStateType>(null);
  const [contactModalState, setContactModalState] =
    useState<ContactModalStateType>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const { md } = useBreakpoint();

  useEffect(() => {
    if (setChatVisibility) {
      setChatVisibility(!visible || md);
    }
  }, [md, visible, setChatVisibility]);

  const isMultiConf =
    contacts?.filter(assignedContact => assignedContact.mustConfirm).length > 1;

  const title = `${contactModalState?.isEditMode ? 'Edit' : 'Add'} Contact`;

  return (
    <Modal
      closable={false}
      open={visible}
      title={
        <StyledConfigModalTitle data-cy={`${kebabCase(title)}-modal-title`}>
          {title}
        </StyledConfigModalTitle>
      }
      width={900}
      destroyOnClose
      zIndex={zPositions.contactModal}
      footer={
        isSaving || !contactState ? null : (
          <ModalFooter
            contactState={contactState}
            contactModalState={contactModalState}
            isSaveButtonDisabled={!isFormValid}
            onSave={() => {
              onSave(
                contactState,
                contactModalState.mode,
                contactModalState.shouldClone,
                contactModalState.isEditMode,
              );
            }}
            onClose={onClose}
          />
        )
      }
    >
      <ContactModalStateContextProvider
        isProfile={isProfile}
        contactForEdit={contact}
        listingId={listingId}
        listing={listing}
        onContactModalStateChange={setContactModalState}
        userId={userId}
        isMustCallDisabled={isMustCallDisabled}
      >
        <ContactStateContextProvider
          contact={contact}
          onContactChange={(updatedContact, updatedIsFormValid) => {
            setContactState(updatedContact);
            setIsFormValid(updatedIsFormValid);
          }}
          isProfile={isProfile}
          addBuyerContact={addBuyerContact}
          feedbackSendToSeller={feedbackSendToSeller}
        >
          <ContactStateContext.Consumer>
            {([state]) => {
              let content: React.ReactNode;
              if (isSaving) {
                content = <SavingLoader />;
              } else if (isProfile || state.type === 'Agent') {
                content = <AgentContent />;
              } else if (isClientContactType(state.type)) {
                content = (
                  <ContactContent
                    contacts={contacts}
                    isMultiConf={isMultiConf}
                  />
                );
              } else if (state.type === 'Concierge') {
                content = (
                  <ConciergeContent
                    conciergeInputsDisabled={
                      !contactModalState?.isEditMode &&
                      contacts.some(c => c.type === 'Concierge')
                    }
                  />
                );
              }

              return content;
            }}
          </ContactStateContext.Consumer>
        </ContactStateContextProvider>
      </ContactModalStateContextProvider>
    </Modal>
  );
};

export default ConfigContactModal;

const StyledConfigModalTitle = styled(Title)`
  && {
    margin-bottom: 0;
    font-size: 32px;
    text-align: center;
  }
`;
